import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type ButtonVariant =
  | 'primary'
  | 'accent'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'danger'
  | 'success';

export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  isLoading?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-navy text-white border-brand-navy hover:bg-brand-navy-light hover:shadow-colored',
  accent:
    'bg-brand-orange text-white border-brand-orange hover:bg-brand-orange-light hover:shadow-accent',
  secondary:
    'bg-white text-ink-primary border-line hover:bg-surface-muted hover:border-line-strong',
  outline:
    'bg-transparent text-ink-primary border-line-strong hover:bg-surface-muted hover:border-brand-navy hover:text-brand-navy',
  ghost:
    'bg-transparent text-ink-secondary border-transparent hover:bg-surface-muted hover:text-ink-primary',
  danger: 'bg-danger text-white border-danger hover:bg-danger-dark',
  success: 'bg-success text-white border-success hover:bg-success-dark',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1 text-sm min-h-[30px] rounded-sm',
  md: 'px-4 py-2 text-base min-h-[36px] rounded',
  lg: 'px-5 py-3 text-lg min-h-[44px] rounded-md',
};

/**
 * Standard button. Six variants × three sizes. Use this everywhere.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'md',
      leftIcon,
      rightIcon,
      isLoading,
      className,
      children,
      disabled,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      disabled={disabled || isLoading}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-semibold leading-tight border whitespace-nowrap select-none',
        'transition-all duration-fast ease-DEFAULT',
        'hover:-translate-y-px',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      {...props}
    >
      {isLoading ? (
        <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        leftIcon
      )}
      {children}
      {!isLoading && rightIcon}
    </button>
  ),
);

Button.displayName = 'Button';
